import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Link, useParams } from 'react-router-dom';

import { useConnection, useConnectionSummaries } from '@/entities/connection';
import { CatalogBrowser } from '@/features/catalog-browser';
import { usePlacements } from '@/features/federation';
import {
  DELETE_SOURCE_PERMISSION,
  DISABLE_SOURCE_PERMISSION,
  ROTATE_CREDENTIAL_PERMISSION,
  can,
  useIdentity,
} from '@/entities/session';

import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { Section, Sections } from '@/shared/ui/Layout';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import {
  datasetsBlockingDelete,
  useDeleteConnection,
  useReplaceCredential,
  useRequestHarvest,
  useSetInService,
  useVerifyConnection,
} from '../api/connections';
import { useConnectors } from '../api/connectors';
import { useReadiness } from '@/features/platform-status';
import { HarvestScheduleCard, isDurable, schedulerHealth } from '@/features/scheduling';
import { AutoProfileCard } from './AutoProfileCard';
import { HarvestProgress } from './HarvestProgress';
import { ReplaceCredentialDialog } from './ReplaceCredentialDialog';
import { SourceActivity } from './SourceActivity';
import { SourceCapabilities } from './SourceCapabilities';

import { capabilitiesOf, reachOf } from '../model/capabilities';
import { harvestReading, lastChecked } from '../model/listing';
import { CONNECTOR_LABEL, addressOf, credentialFacts } from '../model/source';
import {
  CONNECTIONS_QUERY_KEY,
  readyToHarvest,
  tracksRenames,
} from '@/entities/connection';

/* One source: what it is, what this deployment can do with it, what has happened to it, and every
 * action that acts on it.
 *
 * **The actions moved here from the list, and that is the point of the redesign.** Verify, Harvest,
 * Edit credential, Disable and Delete are decisions about *one* source; a grid of cards put five sets
 * of them on one screen, which meant a destructive control sat inside a scroll region next to four
 * others and a page-level failure banner could not say which card it belonged to. The list is now a
 * worklist that ranks and links; this is where a reader acts.
 *
 * **Read-only was the old rule here and it was the wrong one.** The previous version said adding
 * these buttons "would be a second place `SourceCard`'s mutation logic has to be kept in sync" —
 * true while both existed, and the answer is that only one does now.
 *
 * **The datasets section is `CatalogBrowser` itself, scoped rather than reimplemented.** The search
 * box, the PII and score columns and every state they distinguish come from the one table this
 * platform already has for datasets; a second table here would be a second place those distinctions
 * can drift. `key={connectionId}` forces a clean remount when navigating from one source's page
 * straight to another's, so a search term or a pager cursor signed for source A's query can never be
 * carried into source B's.
 */

function Breadcrumbs({ name }: { readonly name: string }): ReactElement {
  const { t } = useTranslation();
  return (
    <nav aria-label={t('breadcrumbs.label')} className="lq-breadcrumbs">
      <Link to={SCREEN_PATHS.connections}>{t('nav.connections')}</Link>
      <span aria-hidden="true" className="lq-breadcrumbs__sep">
        /
      </span>
      {/* `aria-current="page"` rather than a link to here, the same choice `DatasetDetail`'s
          breadcrumb makes: a last crumb that navigates to the page you are on costs a screen-reader
          user a round trip for nothing. */}
      <span aria-current="page">{name}</span>
    </nav>
  );
}

export function ConnectionDetail(): ReactElement {
  const { t } = useTranslation();
  const { connectionId = '' } = useParams<{ connectionId: string }>();
  const connection = useConnection(connectionId);
  const connectors = useConnectors();
  const placements = usePlacements();
  const summaries = useConnectionSummaries();
  const identity = useIdentity();

  const verify = useVerifyConnection();
  const harvest = useRequestHarvest();
  /* **Whether a scheduled harvest survives a restart, read once for the page.**
   *
   * `/readiness` reports only the *degraded* capabilities, so an absent `harvest_scheduler` means
   * it is answering — which is why this goes through `schedulerHealth` rather than looking for a
   * `production` entry that is never sent. A failed read yields `unknown`, which is not durable:
   * a card must not promise durability this screen could not verify. */
  const readiness = useReadiness();
  const durable = isDurable(
    schedulerHealth(readiness.isSuccess ? readiness.data.degraded : null),
    'harvest_scheduler',
  );
  const service = useSetInService();
  const credential = useReplaceCredential();
  const forget = useDeleteConnection();

  const client = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  /* Awaited so the button's busy state covers the actual refetch rather than only the invalidation,
     which returns immediately and would make the control flicker and settle before anything new had
     arrived — a spinner that lies about being finished. */
  const refresh = async (): Promise<void> => {
    setRefreshing(true);
    try {
      await Promise.all([
        client.invalidateQueries({ queryKey: CONNECTIONS_QUERY_KEY }),
        /* The harvest's own effects: the dataset listing this page embeds, the summaries that carry
           the dataset count and last-harvested time, and the audit rows the activity panel reads. */
        client.invalidateQueries({ queryKey: ['catalog'] }),
        client.invalidateQueries({ queryKey: ['audit'] }),
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  const [watching, setWatching] = useState<string | null>(null);
  const [armed, setArmed] = useState(false);
  const [editing, setEditing] = useState(false);

  /* One instant for the whole render, so the status line and the last-checked line cannot differ by
   * a minute — two `new Date()` calls can report one timestamp as "59 minutes" and "1 hour". Not
   * memoised: the factory reads nothing from the query, so a dependency array would be a claim
   * eslint correctly refused, and a plain const is already once per render. */
  const now = new Date();

  if (connection.isPending) {
    return (
      <>
        <p role="status" className="lq-muted">
          {t('connectionDetail.loading')}
        </p>
        <SkeletonRows rows={4} columns={2} />
      </>
    );
  }

  if (connection.isError) {
    return (
      <>
        <Alert tone="error" title={t('connectionDetail.unreachable')}>
          {connection.error.message}
        </Alert>
        <p className="lq-source__note">
          <Link to={SCREEN_PATHS.connections}>
            {t('connectionDetail.backToConnections')}
          </Link>
        </p>
      </>
    );
  }

  /* A successful read that found nothing under this id — a bad link, a retired source, or (at up to
   * 200 connections) one past the list's cap. Not the same as `isError`: the request worked and
   * answered honestly that this id is not among the connections it returned. */
  if (connection.data === undefined) {
    return (
      <>
        <Alert tone="notice" title={t('connectionDetail.notFoundTitle')}>
          {t('connectionDetail.notFoundGuidance')}
        </Alert>
        <p className="lq-source__note">
          <Link to={SCREEN_PATHS.connections}>
            {t('connectionDetail.backToConnections')}
          </Link>
        </p>
      </>
    );
  }

  const source = connection.data;
  const support = capabilitiesOf(source, connectors.data?.items ?? []);
  const reach = reachOf(support);
  const profile = connectors.data?.items.find(
    (entry) => entry.connector_type === source.connector_type,
  );
  const inService = source.state !== 'disabled';
  const credentials = credentialFacts(source);
  const checked = lastChecked(source, now);
  const running = harvestReading(
    summaries.data?.items.find((s) => s.connection_id === source.id),
  );

  const region =
    placements.data?.find((p) => p.connection_id === source.id)?.region_code ?? null;

  /* How many datasets the server's last refusal said are in the way, or `null`. Derived from the
     mutation state rather than held in a `useState`, so it cannot survive a `reset()` and leave a
     stale count armed on a dialog the operator reopened. */
  const blocking = datasetsBlockingDelete(forget.error);

  const unreachable =
    verify.data !== undefined && !verify.data.reachable ? verify.data : null;
  const actionFailure =
    (verify.error ?? harvest.error ?? service.error ?? forget.error)?.message ?? null;

  /* `can` hides controls and protects nothing — every route refuses server-side regardless — and it
     exists so a reader is not offered a door that will slam. Both of these reach `admin` only. */
  const mayDisable = can(identity.data, DISABLE_SOURCE_PERMISSION);
  const mayRotate = can(identity.data, ROTATE_CREDENTIAL_PERMISSION);
  const mayDelete = can(identity.data, DELETE_SOURCE_PERMISSION);

  return (
    <Sections>
      <Breadcrumbs name={source.name} />

      <div className="lq-detail-head">
        <div className="lq-detail-head__identity">
          {/* Mono, because the name is an identifier an operator matches against something written
              down elsewhere — a runbook, a ticket, their own DBA's notes. */}
          <h1 className="lq-detail-head__name lq-mono">{source.name}</h1>
          <p className="lq-detail-head__endpoint lq-mono">{addressOf(source)}</p>
        </div>
        <StatusBadge
          tone={
            source.state === 'verified'
              ? 'ok'
              : source.state === 'failed'
                ? 'danger'
                : source.state === 'disabled'
                  ? 'neutral'
                  : 'info'
          }
          label={t(`connections.state.${source.state}.label`)}
          title={t(`connections.state.${source.state}.meaning`)}
        />
      </div>

      {/* **The banner for a failed source says what the platform actually knows, which is less than
          the reference design implies.** A `failed` state records that a verification was refused;
          the driver's *reason* is deliberately not persisted — `verify`'s own docstring explains that
          a connect error can carry the constructed DSN and sometimes the password — so there is no
          stored sentence to render here. What there is: that it was refused, when (from
          `last_checked_at`), and that verifying again will report why. Inventing a cause would be the
          claim-without-evidence defect on the one screen a reader comes to for a cause. */}
      {source.state === 'failed' ? (
        <Alert tone="error" title={t('connectionDetail.failedTitle')}>
          {checked.kind === 'never'
            ? t('connectionDetail.failedNoDate')
            : t('connectionDetail.failedBody')}
        </Alert>
      ) : null}

      {!inService ? (
        <Alert tone="notice" title={t('connectionDetail.disabledTitle')}>
          {t('connectionDetail.disabledBody')}
        </Alert>
      ) : null}

      {/* The verification's own answer, which is the only place a reason for a refusal exists. Kept
          separate from the state banner above: that one is about a stored state and this one is about
          the request the reader just made. */}
      {unreachable !== null ? (
        <Alert tone="error" title={t('connections.unreachable')}>
          {unreachable.detail}
        </Alert>
      ) : null}
      {verify.data?.reachable === true ? (
        <Alert tone="notice" title={t('connectionDetail.verifiedTitle')}>
          {t('connectionDetail.verifiedBody')}
        </Alert>
      ) : null}
      {actionFailure !== null && blocking === null ? (
        <Alert tone="error" title={t('connections.actionFailed')}>
          {actionFailure}
        </Alert>
      ) : null}

      <div className="lq-detail-actions">
        {/* **Refresh, and it re-reads rather than re-doing anything.** A harvest is a 202: the
            request returns immediately and the datasets, the row count and the activity arrive
            minutes later, so every figure on this page is a snapshot of the moment it loaded. The
            honest control for that is one that refetches — not a second Harvest, which would dial
            the customer's source again to answer a question the platform already has.

            **It invalidates the keys this page reads and no others.** A blanket `invalidateQueries()`
            would refetch every screen's cache including the catalogue walk, which on a large estate
            is a page of work to answer "what changed here". */}
        <Button
          busy={refreshing}
          onClick={() => {
            void refresh();
          }}
        >
          {t('connectionDetail.refresh')}
        </Button>
        <Button
          tone="primary"
          busy={verify.isPending}
          onClick={() => {
            verify.mutate(source.id);
          }}
        >
          {t('connections.verify')}
        </Button>
        <Button
          busy={harvest.isPending}
          /* Harvest stays gated on a verified source, with the reason on the control. Queueing a
             harvest against untested credentials fails minutes later and asynchronously, where the
             failure is much harder to connect back to its cause. A disabled source is refused by the
             server too, so the control says which of the two applies rather than being merely off. */
          disabled={!readyToHarvest(source)}
          title={
            readyToHarvest(source)
              ? undefined
              : inService
                ? t('connections.verifyFirst')
                : t('connectionDetail.harvestOutOfService')
          }
          onClick={() => {
            harvest.mutate(source.id, {
              onSuccess: (operation) => {
                setWatching(operation.id);
              },
            });
          }}
        >
          {t('connections.harvest')}
        </Button>
        {mayRotate ? (
          <Button
            onClick={() => {
              setEditing(true);
            }}
          >
            {t('connectionDetail.credential.open')}
          </Button>
        ) : null}
        {mayDisable ? (
          <Button
            busy={service.isPending}
            onClick={() => {
              service.mutate({ connectionId: source.id, inService: !inService });
            }}
          >
            {inService ? t('connectionDetail.disable') : t('connectionDetail.enable')}
          </Button>
        ) : null}
        {mayDelete ? (
          /* Pushed to the far end, so a destructive control is never adjacent to the one a reader
             presses most. */
          <Button
            tone="danger"
            className="lq-detail-actions__danger"
            onClick={() => {
              setArmed(true);
            }}
          >
            {t('connections.delete')}
          </Button>
        ) : null}
      </div>

      {watching !== null ? (
        <HarvestProgress
          harvestId={watching}
          onDismiss={() => {
            setWatching(null);
          }}
        />
      ) : null}

      <div className="lq-detail-grid">
        <Card title={t('connectionDetail.connection')}>
          <dl className="lq-facts">
            <div className="lq-facts__row">
              <dt className="lq-facts__label">{t('connectionDetail.provider')}</dt>
              <dd className="lq-facts__value">
                {CONNECTOR_LABEL[source.connector_type]}
              </dd>
            </div>
            <div className="lq-facts__row">
              <dt className="lq-facts__label">{t('connections.card.address')}</dt>
              <dd className="lq-facts__value lq-mono">
                {source.host}:{source.port}
              </dd>
            </div>
            <div className="lq-facts__row">
              <dt className="lq-facts__label">{t('connections.field.database')}</dt>
              <dd className="lq-facts__value lq-mono">{source.database}</dd>
            </div>
            <div className="lq-facts__row">
              <dt className="lq-facts__label">{t('connections.card.secret')}</dt>
              {/* Echoed because it is a path, not a secret. Shown so an operator can confirm they
                  pointed at the right entry — the commonest registration mistake by far. */}
              <dd className="lq-facts__value lq-mono lq-dim">{credentials.path}</dd>
            </div>
            <div className="lq-facts__row">
              <dt className="lq-facts__label">{t('connections.card.region')}</dt>
              <dd className="lq-facts__value">
                {!placements.isSuccess ? (
                  /* Pending *or* failed: a region we could not read must not render as "not
                     declared", which is a claim about the tenant's configuration rather than about
                     our request. */
                  <span className="lq-dim">{t('connections.card.regionUnknown')}</span>
                ) : region === null ? (
                  /* Undeclared is a real state with a consequence — an unplaced connection is
                     refused by the residency gate — so it says that rather than showing a blank. */
                  <span className="lq-dim">{t('connections.card.regionUndeclared')}</span>
                ) : (
                  <code>{region}</code>
                )}
              </dd>
            </div>
            <div className="lq-facts__row">
              <dt className="lq-facts__label">{t('connectionDetail.lastChecked')}</dt>
              <dd className="lq-facts__value">{checkedSentence(t, checked)}</dd>
            </div>
          </dl>

          {/* **There is no Username row, and that absence is stated rather than left blank.** The
              reference card has one; this platform has nothing to put in it. The credential is sealed
              into the secret store at registration and `Connection` carries no field any part of it
              could come back in — which is a property of the response type rather than a rule the
              server follows. A dash there would invite a reader to conclude the source has no user. */}
          <p className="lq-source__note">{t('connectionDetail.credentialNote')}</p>

          {!tracksRenames(source) ? (
            <p className="lq-source__note">{t('connections.noRenameAnchorDetail')}</p>
          ) : null}
        </Card>

        <SourceCapabilities support={support} />

        {/* **The harvest schedule sits in the detail grid, beside Facts and Capabilities**, which
            is where the design puts it and where it belongs: a cadence is a property of the source,
            and the reader deciding it is already looking at this source's endpoint and its last
            check. Above Recent activity, because the schedule is configuration and the activity
            list is history. */}
        <HarvestScheduleCard connectionId={connectionId} durable={durable} />
        {/* **Beside the harvest schedule, and after it.** Both are standing instructions about
            this source, and the order is the order somebody decides them in: how often to look
            at the source, then whether looking should also measure what is in it. */}
        <AutoProfileCard connectionId={connectionId} />
      </div>

      {reach === 'unknown' ? (
        <p className="lq-source__note">{t('connectionDetail.capabilitiesUnread')}</p>
      ) : null}

      <SourceActivity connectionId={source.id} now={now} />

      <Section title={t('connectionDetail.datasetsTitle')}>
        {running.kind === 'running' ? (
          <p className="lq-source__note">
            {t('connections.harvesting', {
              observed: running.observed,
              discovered: running.discovered,
            })}
          </p>
        ) : null}
        <CatalogBrowser
          key={connectionId}
          connectionId={connectionId}
          standalone={false}
        />
      </Section>

      <ReplaceCredentialDialog
        open={editing}
        connection={source}
        profile={profile}
        busy={credential.isPending}
        error={credential.error?.message}
        onSubmit={(values) => {
          credential.mutate(
            { connectionId: source.id, credentials: values },
            {
              onSuccess: () => {
                setEditing(false);
                /* Reset once the dialog has closed, so no submitted credential is left on the
                   mutation observer's `variables` — React Query keeps it there until reset, doing
                   exactly what it should with no idea one of the values is a password. */
                credential.reset();
              },
            },
          );
        }}
        onClose={() => {
          setEditing(false);
          credential.reset();
        }}
      />

      {/* **Two presses when the source has datasets, and the second one carries a number.** The first
          press asks for the plain delete. If the source still has datasets the server refuses and
          *states how many* — as a field, not only in the sentence — so the dialog can then offer to
          retire the source together with exactly that many. The operator sees the count before
          agreeing to it, and the number they agree to is the one the server reported microseconds
          earlier, which is what makes the server's own count check pass.

          **Not a checkbox, and not a pre-fetched count.** A checkbox armed before the refusal would
          let somebody retire 148 tables having read nothing about them, and a count fetched when the
          page rendered could be minutes stale — the server would refuse it, correctly, and the
          operator would have no idea why. Reading it off the refusal makes the fresh count the only
          one reachable. */}
      <ConfirmDialog
        open={armed}
        title={t('connections.deleteTitle')}
        message={
          blocking === null
            ? t('connections.deleteAsk', { name: source.name })
            : t('connections.deleteWithDatasetsAsk', {
                name: source.name,
                datasets: blocking,
              })
        }
        confirmLabel={
          blocking === null
            ? t('connections.delete')
            : t('connections.deleteWithDatasets', { datasets: blocking })
        }
        cancelLabel={t('common.cancel')}
        tone="danger"
        busy={forget.isPending}
        /* The refusal that *offers* the cascade is not shown as an error: it is the question being
           re-asked with a number in it, and a red banner above a button labelled "retire it and its
           8 datasets" would read as a failure the operator then confirmed. Every other refusal —
           including a stale count — still shows. */
        error={forget.isError && blocking === null ? forget.error.message : undefined}
        onConfirm={() => {
          forget.mutate(
            blocking === null
              ? { connectionId: source.id }
              : { connectionId: source.id, retireDatasets: blocking },
            {
              onSuccess: () => {
                setArmed(false);
              },
            },
          );
        }}
        onCancel={() => {
          setArmed(false);
          /* Resets the mutation, so re-opening the dialog does not show the previous refusal.
             Without it a reader who cancels and reconsiders is met by an error about the attempt they
             abandoned. */
          forget.reset();
        }}
      />
    </Sections>
  );
}

/* i18next's own function type rather than a hand-written signature. A narrower local alias
 * type-checks at the definition and not at the call site — `t` carries overloads for the
 * `defaultValue` forms this file uses, and a simplified alias silently drops them under
 * `exactOptionalPropertyTypes`. */
type Translate = TFunction;

/* The last-checked sentence, in the card's own voice rather than the table's compact one.
 *
 * The table says "41 d ago" because it is scanned; the card says "41 days ago" because it is read.
 * One reading, two renderings, and the reading is `lastChecked` in both — a second bucketing would let
 * the two disagree about when an hour becomes hours.
 */
function checkedSentence(t: Translate, checked: ReturnType<typeof lastChecked>): string {
  switch (checked.kind) {
    case 'never':
      return t('connectionDetail.checked.never');
    case 'justNow':
      return t('connectionDetail.checked.justNow');
    case 'minutes':
      return t('connectionDetail.checked.minutes', { count: checked.count });
    case 'hours':
      return t('connectionDetail.checked.hours', { count: checked.count });
    default:
      return t('connectionDetail.checked.days', { count: checked.count });
  }
}
